'use client';

import React, { useState } from 'react';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  ChevronDown,
  CheckCircle2,
  Send,
  Shield,
  Gauge
} from 'lucide-react';
import { FAQS } from '@/lib/data';
import { usePublicBrand } from '@/components/site/PublicBrandProvider';

interface AboutContactViewProps {
  initialTab?: 'about' | 'contact';
  faq?: { question: string; answer: string }[];
  hours?: { label: string; value: string }[];
  /** Hidden when the appearance editor disables the FAQ section. */
  showFaq?: boolean;
}

export function AboutContactView({ initialTab = 'about', faq, hours, showFaq = true }: AboutContactViewProps) {
  const [activeTab, setActiveTab] = useState<'about' | 'contact'>(initialTab);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const brand = usePublicBrand();

  // Contact form
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('General Inquiries');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name && email && message) {
      setSubmitted(true);
      setTimeout(() => {
        setName('');
        setEmail('');
        setMessage('');
      }, 4000);
    }
  };

  return (
    <div className="w-full bg-slate-50 min-h-screen py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        {/* Sub-navigation tabs */}
        <div className="flex items-center gap-3 border-b border-slate-200 mb-8">
          <button
            type="button"
            onClick={() => setActiveTab('about')}
            className={`pb-3 px-3 text-xs font-bold tracking-wider uppercase transition-all cursor-pointer relative ${
              activeTab === 'about' ? 'text-[var(--color-primary)]' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <span>About fleetava</span>
            {activeTab === 'about' && (
              <span className="absolute bottom-0 left-0 w-full h-[2px] bg-[var(--color-primary)]" />
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('contact')}
            className={`pb-3 px-3 text-xs font-bold tracking-wider uppercase transition-all cursor-pointer relative ${
              activeTab === 'contact' ? 'text-[var(--color-primary)]' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <span>Contact & Support</span>
            {activeTab === 'contact' && (
              <span className="absolute bottom-0 left-0 w-full h-[2px] bg-[var(--color-primary)]" />
            )}
          </button>
        </div>

        {/* TAB: ABOUT US */}
        {activeTab === 'about' && (
          <div className="space-y-12">
            {/* Story & Mission */}
            <div className="bg-white rounded-3xl border border-slate-200 p-8 sm:p-12 shadow-xs">
              <div className="max-w-3xl">
                <span className="text-xs font-black tracking-wider uppercase text-[var(--color-primary)] block mb-1">
                  OUR STORY
                </span>
                <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
                  Redefining Freedom on The Open Road
                </h2>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed mb-4">
                  Founded with a vision to eliminate rental car counter friction, hidden surcharges, and confusing insurance terms, <strong>fleetava Car Rental</strong> is dedicated to delivering seamless mobility experiences across North America.
                </p>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed mb-8">
                  Whether you need a fuel-efficient Toyota Corolla for city meetings, a rugged Hyundai Tucson SUV for mountain adventures, or a BMW 5 Series executive sedan for special events, our meticulously maintained fleet is ready when you are.
                </p>

                {/* 3 Core Pillars */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6 border-t border-slate-100">
                  <div>
                    <span className="text-2xl font-black text-[var(--color-primary)] font-mono">100%</span>
                    <h4 className="text-xs font-bold text-slate-900 mt-1">Zero Hidden Fees</h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">Transparent total prices calculated upfront.</p>
                  </div>
                  <div>
                    <span className="text-2xl font-black text-[var(--color-primary)] font-mono">24/7</span>
                    <h4 className="text-xs font-bold text-slate-900 mt-1">Roadside Peace of Mind</h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">Dedicated dispatchers whenever you need help.</p>
                  </div>
                  <div>
                    <span className="text-2xl font-black text-[var(--color-primary)] font-mono">0 Min</span>
                    <h4 className="text-xs font-bold text-slate-900 mt-1">Free Cancellation</h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">Change or cancel your plans risk-free.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Accordion */}
            {showFaq && (
              <div className="bg-white rounded-3xl border border-slate-200 p-8 sm:p-10 shadow-xs">
                <div className="mb-6">
                  <span className="text-xs font-black tracking-wider uppercase text-[var(--color-primary)] block mb-1">
                    FREQUENTLY ASKED QUESTIONS
                  </span>
                  <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight">
                    Everything You Need to Know
                  </h3>
                </div>

              <div className="space-y-3">
                {(faq && faq.length > 0 ? faq.map((row) => ({ q: row.question, a: row.answer })) : FAQS).map((item, idx) => {
                  const isOpen = openFaq === idx;
                  return (
                    <div
                      key={idx}
                      className="border border-slate-200 rounded-2xl overflow-hidden transition-all"
                    >
                      <button
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? null : idx)}
                        className="w-full p-4 text-left flex items-center justify-between gap-4 font-bold text-xs sm:text-sm text-slate-900 hover:text-[var(--color-primary)] transition-colors cursor-pointer"
                      >
                        <span>{item.q}</span>
                        <ChevronDown
                          className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${
                            isOpen ? 'rotate-180 text-[var(--color-primary)]' : ''
                          }`}
                        />
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-4 pt-1 text-xs text-slate-500 leading-relaxed border-t border-slate-100 bg-slate-50/50">
                          {item.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              </div>
            )}
          </div>
        )}

        {/* TAB: CONTACT US */}
        {activeTab === 'contact' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Contact Information Cards (Span 5) */}
            <div className="lg:col-span-5 space-y-4">
              <div className="bg-[#11161f] text-white rounded-3xl p-6 sm:p-8 border border-slate-800 shadow-xl space-y-6">
                <div>
                  <h3 className="text-xl font-extrabold tracking-tight">
                    Get in Touch With Us
                  </h3>
                  <p className="text-xs text-slate-400 mt-1">
                    Have questions about an upcoming reservation or need special corporate fleet assistance?
                  </p>
                </div>

                <div className="space-y-4 text-xs">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-amber-500/20 text-[var(--color-primary)] flex items-center justify-center shrink-0">
                      <Phone className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase font-bold block">Toll-Free Phone</span>
                      <a href={brand.phone ? `tel:${brand.phone.replace(/\s/g, '')}` : undefined} className="font-semibold text-white hover:text-amber-400">
                        {brand.phone || '—'}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-amber-500/20 text-[var(--color-primary)] flex items-center justify-center shrink-0">
                      <Mail className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase font-bold block">Email Inquiries</span>
                      <a href={brand.email ? `mailto:${brand.email}` : undefined} className="font-semibold text-white hover:text-amber-400">
                        {brand.email || '—'}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-amber-500/20 text-[var(--color-primary)] flex items-center justify-center shrink-0">
                      <MapPin className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase font-bold block">Headquarters</span>
                      <span className="font-semibold text-white">
                        {[brand.address, brand.city].filter(Boolean).join(', ') || '—'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-amber-500/20 text-[var(--color-primary)] flex items-center justify-center shrink-0">
                      <Clock className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase font-bold block">Support Availability</span>
                      <span className="font-semibold text-white">
                        {hours && hours.length > 0 ? hours.map((row) => `${row.label} ${row.value}`).join(' · ') : '24 Hours / 7 Days a Week'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Interactive Contact Message Form (Span 7) */}
            <div className="lg:col-span-7 bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-xs">
              <h3 className="text-lg font-extrabold text-slate-900 mb-1">
                Send Us a Message
              </h3>
              <p className="text-xs text-slate-500 mb-6">
                Fill out the form below and our customer response team will get back to you within 30 minutes.
              </p>

              {submitted ? (
                <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-2xl text-center space-y-2">
                  <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
                  <h4 className="text-base font-bold text-slate-900">Message Sent Successfully!</h4>
                  <p className="text-xs text-slate-600">
                    Thank you, {name}. A fleetava service specialist has received your inquiry and will email you shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 text-xs">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="font-bold text-slate-700 block mb-1">Your Name</label>
                      <input
                        type="text"
                        required
                        placeholder="John Doe"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                      />
                    </div>
                    <div>
                      <label className="font-bold text-slate-700 block mb-1">Email Address</label>
                      <input
                        type="email"
                        required
                        placeholder="john@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="font-bold text-slate-700 block mb-1">Inquiry Subject</label>
                    <select
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-semibold"
                    >
                      <option value="General Inquiries">General Inquiries</option>
                      <option value="Existing Reservation Help">Existing Reservation Help</option>
                      <option value="Corporate Fleet Leasing">Corporate Fleet Leasing</option>
                      <option value="Billing & Invoicing">Billing & Invoicing</option>
                    </select>
                  </div>

                  <div>
                    <label className="font-bold text-slate-700 block mb-1">Your Message</label>
                    <textarea
                      required
                      rows={4}
                      placeholder="How can we assist you with your rental journey?"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] active:scale-95 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Send className="w-4 h-4" />
                    <span>Send Message to Support</span>
                  </button>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
